/**
 * One way to open an opportunity from anywhere in the app.
 *
 * The editable view lives in /oportunidades, an admin-only route: a non-admin
 * sent there is bounced to /profile by ProtectedRoute and nothing opens. So:
 *
 *  - Admins navigate to /oportunidades with `location.state.openOpportunityId`
 *    (OportunidadesPage opens the edit modal for that id, then clears the state).
 *  - Everyone else gets the read-only OpportunitySummaryModal through the
 *    caller's `onReadOnly` callback. Without one, nothing happens: a
 *    non-admin is never navigated to an admin-only route.
 *
 * /oportunidades/:id also exists as a bookmarkable alias (App.tsx) that
 * redirects admins to the same state, but in-app code should use this helper.
 */

import type { NavigateFunction } from 'react-router-dom'

export const OPPORTUNITIES_PATH = '/oportunidades'

/** Shape of the navigation state OportunidadesPage understands. */
export interface OpenOpportunityState {
    openOpportunityId: number
}

export const isAdminRole = (role?: string | null): boolean => role?.toLowerCase() === 'admin'

/** Parse an id coming from an API row, a route param or a card (string or number). */
export const toOpportunityId = (id: number | string | null | undefined): number | null => {
    if (id === null || id === undefined || id === '') return null
    const n = typeof id === 'number' ? id : Number(id)
    return Number.isInteger(n) && n > 0 ? n : null
}

export interface OpenOpportunityOptions {
    isAdmin: boolean
    /** Opens the read-only summary (typically `setSummaryOppId`). Used for non-admins. */
    onReadOnly?: (id: number) => void
}

/**
 * Open an opportunity following the contract above. Returns true when
 * something was opened, so callers can tell whether the click did anything.
 */
export const openOpportunity = (
    navigate: NavigateFunction,
    id: number | string | null | undefined,
    { isAdmin, onReadOnly }: OpenOpportunityOptions,
): boolean => {
    const oppId = toOpportunityId(id)
    if (oppId === null) return false
    if (isAdmin) {
        const state: OpenOpportunityState = { openOpportunityId: oppId }
        navigate(OPPORTUNITIES_PATH, { state })
        return true
    }
    if (onReadOnly) {
        onReadOnly(oppId)
        return true
    }
    return false
}

/** True when a click on an opportunity can open something for this user. */
export const canOpenOpportunity = ({ isAdmin, onReadOnly }: OpenOpportunityOptions): boolean =>
    isAdmin || !!onReadOnly
